import * as Sentry from '@sentry/react';
/** Frontend error reporting. Fire-and-forget POST to /api/errors with
 *  message + stack + url + UA. Deduplicates the SAME message within a
 *  60-second window so a render-loop doesnt post 500 copies. */
const _recent = new Map<string, number>(); // dedupe key -> last sent ms
const DEDUPE_MS = 60_000;

function dedupeKey(message: string, stack: string): string {
  // First line of stack is usually unique enough; fall back to message.
  const sigLine = stack.split('\n').find((l) => l.trim().length > 0) || message;
  return `${message}::${sigLine}`.slice(0, 200);
}

export function reportError(opts: {
  message: string;
  stack?: string;
  section?: string;
  url?: string;
}) {
  try {
    const message = String(opts.message || '').slice(0, 500);
    if (!message) return;
    const stack = String(opts.stack || '').slice(0, 4000);

    // Dedupe so a render-loop or repeat-rerender doesnt spam the log.
    const key = dedupeKey(message, stack);
    const now = Date.now();
    const last = _recent.get(key) || 0;
    if (now - last < DEDUPE_MS) return;
    _recent.set(key, now);

    // Fire and forget. Use fetch directly so we dont depend on the api
    // wrapper (the api wrapper might be where the error came from).
    fetch('/api/errors', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        message,
        stack,
        section: opts.section,
        url: opts.url || (typeof window !== 'undefined' ? window.location.href : ''),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      }),
      keepalive: true,
    }).catch(() => { /* swallow — we cant report a reporting failure */ });
  } catch {
    /* never throw from the reporter */
  }
}

/** Wire window.onerror + window.unhandledrejection to send anything that
 *  escapes React. Call once from main.tsx at app boot. */
export function setupGlobalErrorReporting() {
  if (typeof window === 'undefined') return;
  window.addEventListener('error', (e) => {
    const err = e.error;
    reportError({
      message: err?.message || e.message || 'window.error',
      stack: err?.stack,
      section: 'window.onerror',
    });
  });
  window.addEventListener('unhandledrejection', (e) => {
    const r = e.reason;
    reportError({
      message: r?.message || String(r) || 'unhandledrejection',
      stack: r?.stack,
      section: 'unhandledrejection',
    });
  });
}
